import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { useReturnToUrl } from "../../../../../core/router/navigation/returnTo";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useContractVirtualLoadQuery, useContractVirtualUpdateMutation } from "../../services/server/ContractApi";
import type { ContractEntity } from "../../types/ContractEntity";

export const ContractVirtualEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-virtual");

    const [formData, setFormData] = useState<ContractEntity>({} as ContractEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useContractVirtualLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    const onChangeCallback = useCallback(
        (data: ContractEntity) => {
            setFormData({
                ...data,
                acquirerIdCheckReadOnly: true,
                physicalAppProfilesCheckReadOnly: true,
                acceptedCardProfilesCheckReadOnly: true,
            });
        },
        [setFormData],
    );

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractVirtualUpdateMutation();
    const listPath = useReturnToUrl(`${BASE_PATH}/${ResourceName.CONTRACTS}`);
    const completeSubmit = useFormSubmitCompletion(formAction, listPath);
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "Contract Virtual" }));
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractEntity>
                    name="ContractVirtualEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
